import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/lib/auth";
import { ROLE_LABEL, formatDate } from "@/lib/utils-format";
import { Plus, Trash, ShieldStar, X, PencilSimple } from "@phosphor-icons/react";
import Modal from "@/components/Modal";

export default function Admins() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const { admin: current } = useAuth();

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admins");
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Удалить администратора?")) return;
    await api.delete(`/admins/${id}`);
    load();
  };

  const isSuper = current?.role === "super_admin";

  return (
    <div data-testid="admins-page">
      <PageHeader
        title="Администраторы"
        subtitle="Учётные записи для входа в CRM"
        actions={
          isSuper && (
            <button
              data-testid="create-admin-btn"
              onClick={() => setEditing({})}
              className="inline-flex items-center gap-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium px-3 py-2 rounded-md"
            >
              <Plus size={14} weight="bold" /> Добавить админа
            </button>
          )
        }
      />

      {loading ? (
        <div className="text-zinc-500 text-sm">Загрузка…</div>
      ) : (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="admins-table">
            <thead>
              <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                <th className="text-left px-4 py-3 font-medium">Админ</th>
                <th className="text-left px-4 py-3 font-medium">Логин</th>
                <th className="text-left px-4 py-3 font-medium">Роль</th>
                <th className="text-left px-4 py-3 font-medium">Создан</th>
                <th className="text-right px-4 py-3 font-medium">Действия</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr
                  key={a.id}
                  data-testid={`admin-row-${a.id}`}
                  className="border-b border-zinc-900"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white flex items-center justify-center text-xs font-bold">
                        {(a.full_name || a.login)[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium">
                          {a.full_name || a.login}
                          {a.id === current?.id && (
                            <span className="ml-2 text-[10px] font-mono text-zinc-500 uppercase">
                              · вы
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-zinc-400">
                    {a.login}
                  </td>
                  <td className="px-4 py-3">
                    <RoleBadge role={a.role} />
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">
                    {formatDate(a.created_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isSuper && (
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => setEditing(a)}
                          data-testid={`edit-admin-${a.id}`}
                          className="text-zinc-500 hover:text-blue-400 p-1.5"
                        >
                          <PencilSimple size={14} />
                        </button>
                        {a.id !== current?.id && (
                          <button
                            onClick={() => remove(a.id)}
                            data-testid={`delete-admin-${a.id}`}
                            className="text-zinc-500 hover:text-red-400 p-1.5"
                          >
                            <Trash size={14} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <AdminModal
          admin={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function RoleBadge({ role }) {
  const colors = {
    super_admin: "#3b82f6",
    admin: "#2AABEE",
    viewer: "#a1a1aa",
  };
  const c = colors[role] || "#a1a1aa";
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wide border"
      style={{ background: `${c}15`, color: c, borderColor: `${c}40` }}
    >
      <ShieldStar size={9} weight="fill" />
      {ROLE_LABEL[role] || role}
    </span>
  );
}

function AdminModal({ admin, onClose, onSaved }) {
  const isNew = !admin.id;
  const [form, setForm] = useState({
    login: admin.login || "",
    password: "",
    full_name: admin.full_name || "",
    role: admin.role || "admin",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      if (isNew) {
        if (!form.password) throw new Error("Введите пароль");
        await api.post("/admins", form);
      } else {
        const body = {
          full_name: form.full_name,
          role: form.role,
        };
        if (form.password) body.password = form.password;
        await api.put(`/admins/${admin.id}`, body);
      }
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow={isNew ? "Новый администратор" : "Редактирование"}
      title={isNew ? "Новый администратор" : admin.login}
      testid="admin-modal"
      maxWidth="md"
      footer={
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm rounded-md border border-zinc-800 hover:border-zinc-700"
          >
            Отмена
          </button>
          <button
            data-testid="save-admin-btn"
            disabled={saving}
            onClick={save}
            className="px-3 py-2 text-sm rounded-md bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white font-medium"
          >
            {saving ? "Сохранение…" : "Сохранить"}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        {isNew && (
          <Field label="Логин">
            <input
              data-testid="admin-login-input"
              value={form.login}
              onChange={(e) => setForm({ ...form, login: e.target.value })}
              className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm font-mono"
            />
          </Field>
        )}
        <Field label="Имя">
          <input
            data-testid="admin-fullname-input"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Роль">
          <select
            data-testid="admin-role-select"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
          >
            <option value="super_admin">Главный админ</option>
            <option value="admin">Админ</option>
            <option value="viewer">Только просмотр</option>
          </select>
        </Field>
        <Field
          label={isNew ? "Пароль" : "Новый пароль (оставьте пустым, чтобы не менять)"}
        >
          <input
            data-testid="admin-password-input"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
          />
        </Field>
        {err && (
          <div className="text-sm text-red-400" data-testid="admin-error">
            {err}
          </div>
        )}
      </div>
    </Modal>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-zinc-400 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
